import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon';
import BrandIcon from './BrandIcon';
import { getUserRole } from '../utils/rolePermissions';
import { useModal } from '../context/ModalContext';
import { useOrgBrand } from '../context/OrgBrandContext';
import RecipientSelectorModal from './RecipientSelectorModal';
import CommentModal from './CommentModal';
import MonthlyProgressTracker from './MonthlyProgressTracker';
import { useSortableTable, sortData } from '../hooks/useSortableTable';
import '../styles/IdeasPanel.css';
import CustomSelect from './CustomSelect';

const WORD_COUNT_RANGES = {
  social: { short: '50-150', medium: '150-250', long: '250-350' },
  blog: { short: '400-750', medium: '750-1,250', long: '1,250-2,000' },
};

const IdeasPanel = ({ user, isProxyMode = false }) => {
  const modal = useModal();
  const { selectedBrand, selectedOrganization } = useOrgBrand();
  const [ideasDocs, setIdeasDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdeas, setSelectedIdeas] = useState({});   // neutral (checkbox)
  const [approvedIdeas, setApprovedIdeas] = useState({});   // ✓ staged for send
  const [discardedIdeas, setDiscardedIdeas] = useState({}); // ✗ staged for discard
  const [selectedArchivedIdeas, setSelectedArchivedIdeas] = useState({});   // neutral (checkbox) on the Archive tab
  const [approvedArchivedIdeas, setApprovedArchivedIdeas] = useState({});   // ✓ staged for restore
  const [discardedArchivedIdeas, setDiscardedArchivedIdeas] = useState({}); // ✗ staged for discard
  const [commentModalState, setCommentModalState] = useState({ open: false, docId: null, ideaId: null, comments: [] });
  const [filterStatus, setFilterStatus] = useState('all');
  const [sendingToClient, setSendingToClient] = useState(false);
  const [recipientModal, setRecipientModal] = useState({ open: false, docId: null, ideaIds: [], allDocIds: null });

  // Tab state
  const [activeTab, setActiveTab] = useState(() => {
    // Check if navigation from to-do list forced a specific tab
    const forcedTab = sessionStorage.getItem('force_ideas_panel_tab');
    if (forcedTab) {
      sessionStorage.removeItem('force_ideas_panel_tab');
      return forcedTab;
    }
    return localStorage.getItem('ideas_panel_active_tab') || 'current';
  });

  useEffect(() => {
    localStorage.setItem('ideas_panel_active_tab', activeTab);
  }, [activeTab]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Pagination state for history
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_ITEMS_PER_PAGE = 50;

  // Edit modal state
  const [editModal, setEditModal] = useState({ open: false, docId: null, idea: null });
  const [editForm, setEditForm] = useState({
    title: '',
    synopsis: '',
    content_type: '',
    length: 'medium',
    key_points: [],
    sources: [],
    additional_source_ids: []
  });
  // Snapshot of editForm as loaded, so Save can stay disabled until something
  // actually differs - opening and re-saving untouched must not count as an edit.
  const [editFormOriginal, setEditFormOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clearingFlag, setClearingFlag] = useState(false);
  const [sourcePicker, setSourcePicker] = useState({ open: false, loading: false, available: [], selectedIds: [] });
  const [sourceViewer, setSourceViewer] = useState({ open: false, loading: false, source: null, copied: false });

  // Extract the actual IDs we use for filtering
  const brandId = selectedBrand && selectedBrand !== 'all' ? (selectedBrand._id || selectedBrand.id) : null;
  const orgId = selectedOrganization && selectedOrganization !== 'all' ? (selectedOrganization._id || selectedOrganization.id) : null;

  useEffect(() => {
    fetchIdeas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, orgId]);

  useEffect(() => {
    if (activeTab === 'history') {
      setHistoryPage(1); // Reset to page 1 when switching to history
      fetchActivityLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, brandId, orgId]);

  const { sortedData: sortedActivityLogs, requestSort: requestHistorySort, getSortClass: getHistorySortClass } = useSortableTable(activityLogs, { key: 'actionDate', direction: 'desc' });
  const [opIdeasSortConfig, setOpIdeasSortConfig] = useState({ key: 'actionDate', direction: 'desc' });
  const [archiveSortConfig, setArchiveSortConfig] = useState({ key: 'actionDate', direction: 'desc' });

  const fetchIdeas = async () => {
    try {
      setLoading(true);

      // Build query parameters based on selected brand/org
      const params = {};
      if (selectedBrand && selectedBrand !== 'all') {
        const brandId = selectedBrand._id || selectedBrand.id;
        params.brand_id = brandId;
      }
      if (selectedOrganization && selectedOrganization !== 'all') {
        const orgId = selectedOrganization._id || selectedOrganization.id;
        params.organization_id = orgId;
      }

      const response = await axios.get('/api/ideas', {
        params,
        withCredentials: true
      });
      setIdeasDocs(response.data.ideas || []);
    } catch (err) {
      console.error('Error fetching ideas:', err);
      modal.error(err.response?.data?.error || 'Failed to load ideas');
    } finally {
      setLoading(false);
    }
  };

  // Update one or more ideas' status directly in local state instead of re-fetching
  // from the server - keeps the table mounted and avoids a full-panel loading flash
  // for what's otherwise an instant change. updatesByDocId: { docId: [ideaId, ...] }
  const updateIdeasStatusLocally = (updatesByDocId, newStatus, extraFields = {}) => {
    setIdeasDocs(prevDocs => prevDocs.map(doc => {
      const ideaIds = updatesByDocId[doc._id];
      if (!ideaIds || ideaIds.length === 0) return doc;
      return {
        ...doc,
        ideas: doc.ideas.map(idea =>
          ideaIds.includes(idea.id) ? { ...idea, status: newStatus, ...extraFields } : idea
        )
      };
    }));
  };

  const fetchActivityLogs = async () => {
    try {
      setLoadingLogs(true);

      // Build query parameters based on selected brand/org
      const params = {};
      if (selectedBrand && selectedBrand !== 'all') {
        const brandId = selectedBrand._id || selectedBrand.id;
        params.brand_id = brandId;
      }
      if (selectedOrganization && selectedOrganization !== 'all') {
        const orgId = selectedOrganization._id || selectedOrganization.id;
        params.organization_id = orgId;
      }

      // Fetch all ideas, then filter to show only actioned ones
      const response = await axios.get('/api/ideas', {
        params,
        withCredentials: true
      });
      const allIdeas = response.data.ideas || [];

      // Flatten and filter to show only ideas with actioned statuses (include sent_to_client as it's an action taken)
      const actionedIdeas = [];
      allIdeas.forEach(doc => {
        (doc.ideas || []).forEach(idea => {
          if (['sent_to_client', 'approved', 'denied', 'revision_requested', 'approved_internal', 'rejected_internal', 'archived'].includes(idea.status)) {
            actionedIdeas.push({
              ...idea,
              docId: doc._id,
              brandName: doc.brand?.name || doc.brand?.brand_name,
              brandLogo: doc.brand?.icon_url,
              organizationName: doc.organization?.name || doc.organization?.organization_name,
              // For history: use action date (when status was changed), fallback to when sent or generated
              // responded_at is set when client responds, client_decision_at is set via operator update path
              actionDate: idea.responded_at || idea.client_decision_at || idea.sent_to_client_at || idea.updated_at || doc.generated_at,
              generatedAt: doc.generated_at
            });
          }
        });
      });

      setActivityLogs(actionedIdeas);
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setActivityLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getActionIcon = (action) => {
    const iconMap = {
      'idea_approved_internal': 'check',
      'idea_rejected_internal': 'close-x',
      'idea_sent_to_client': 'send',
      'idea_approved': 'check',
      'idea_denied': 'close-x',
      'idea_revision_requested': 'refresh',
      'idea_comment_added': 'message'
    };
    return iconMap[action] || 'circle';
  };

  // Green Light brands auto-approve instantly on send, so there's no client to
  // route the item to - skip the recipient picker entirely for those brands.
  const isGreenLightDoc = (docId) => {
    const doc = ideasDocs.find(d => d._id === docId);
    return !!(doc && doc.brand && doc.brand.green_light_mode === 'green');
  };

  const handleSendToClient = (docId) => {
    const neutral = selectedIdeas[docId] || [];
    const approved = approvedIdeas[docId] || [];
    const ids = [...new Set([...neutral, ...approved])];
    if (ids.length === 0) {
      modal.warning('Please select at least one idea to send');
      return;
    }

    if (isGreenLightDoc(docId)) {
      handleRecipientsSelected([], { docId, ideaIds: ids, allDocIds: null });
      return;
    }

    // Open recipient selector instead of confirmation
    setRecipientModal({
      open: true,
      docId: docId,
      ideaIds: ids
    });
  };

  // Posts one send-to-client request. Returns the response data either way -
  // a requires_confirmation response is not an error, just a pending decision.
  const postSendIdeas = (currentDocId, ids, recipientIds, overrideFlags) => {
    return axios.post(`/api/ideas/${currentDocId}/send-to-client`, {
      idea_ids: ids,
      recipient_ids: recipientIds,
      ...(overrideFlags ? { confirm_override_flags: true } : {})
    }, { withCredentials: true }).then(res => res.data);
  };

  const confirmFlaggedOverride = (flaggedItems) => {
    const list = flaggedItems.map(i => `- ${i.title}`).join('\n');
    return modal.confirm(
      `${flaggedItems.length} item(s) are flagged for review and have not been confirmed as grounded in the source material:\n\n${list}\n\nSend anyway?`,
      'Flagged Content',
      { confirmText: 'Send Anyway', cancelText: 'Cancel' }
    );
  };

  const handleRecipientsSelected = async (recipientIds, context = recipientModal) => {
    const { docId, ideaIds, allDocIds } = context;

    try {
      setSendingToClient(true);

      const nowIso = new Date().toISOString();

      if (allDocIds && allDocIds.length > 1) {
        const updatesByDocId = {};
        const pendingConfirmation = []; // { currentDocId, ids }
        const allFlaggedItems = [];

        // Pass 1: send everything that doesn't need confirmation; defer the rest.
        for (const currentDocId of allDocIds) {
          const neutral = selectedIdeas[currentDocId] || [];
          const approved = approvedIdeas[currentDocId] || [];
          const ids = [...new Set([...neutral, ...approved])];
          if (ids.length === 0) continue;

          const data = await postSendIdeas(currentDocId, ids, recipientIds, false);
          if (data.requires_confirmation) {
            pendingConfirmation.push({ currentDocId, ids });
            allFlaggedItems.push(...data.flagged_items);
          } else {
            updatesByDocId[currentDocId] = ids;
          }
        }

        // Pass 2: if anything was deferred, ask once for the whole batch.
        if (pendingConfirmation.length > 0) {
          const proceed = await confirmFlaggedOverride(allFlaggedItems);
          if (proceed) {
            for (const { currentDocId, ids } of pendingConfirmation) {
              await postSendIdeas(currentDocId, ids, recipientIds, true);
              updatesByDocId[currentDocId] = ids;
            }
          }
        }

        const totalSent = Object.values(updatesByDocId).reduce((sum, ids) => sum + ids.length, 0);
        if (totalSent > 0) {
          const isGreenLight = recipientIds.length === 0;
          const sentMessage = isGreenLight
            ? `Sent ${totalSent} idea(s) - instantly approved (Green Light)`
            : `Sent ${totalSent} idea(s) to ${recipientIds.length} recipient(s)`;
          modal.toast(sentMessage, 'success');
          updateIdeasStatusLocally(updatesByDocId, isGreenLight ? 'approved' : 'sent_to_client', isGreenLight ? {
            sent_to_client_at: nowIso,
            response_status: 'approved',
            approved_at: nowIso,
            approved_by: 'system',
            auto_approved: true
          } : { sent_to_client_at: nowIso });
        }

        // Only clear selection for docs that actually got sent - leave anything
        // the operator declined to override still staged for them to decide on.
        const clearedState = {};
        Object.keys(updatesByDocId).forEach(id => { clearedState[id] = []; });
        setSelectedIdeas(prev => ({ ...prev, ...clearedState }));
        setApprovedIdeas(prev => ({ ...prev, ...clearedState }));
      } else {
        let data = await postSendIdeas(docId, ideaIds, recipientIds, false);

        if (data.requires_confirmation) {
          const proceed = await confirmFlaggedOverride(data.flagged_items);
          if (!proceed) {
            setRecipientModal({ open: false, docId: null, ideaIds: [], allDocIds: null });
            return;
          }
          data = await postSendIdeas(docId, ideaIds, recipientIds, true);
        }

        const isGreenLight = recipientIds.length === 0;
        const sentMessage = isGreenLight
          ? `Sent ${ideaIds.length} idea(s) - instantly approved (Green Light)`
          : `Sent ${ideaIds.length} idea(s) to ${recipientIds.length} recipient(s)`;
        modal.toast(sentMessage, 'success');
        updateIdeasStatusLocally({ [docId]: ideaIds }, isGreenLight ? 'approved' : 'sent_to_client', isGreenLight ? {
          sent_to_client_at: nowIso,
          response_status: 'approved',
          approved_at: nowIso,
          approved_by: 'system',
          auto_approved: true
        } : { sent_to_client_at: nowIso });

        setSelectedIdeas(prev => ({ ...prev, [docId]: [] }));
        setApprovedIdeas(prev => ({ ...prev, [docId]: [] }));
      }

      setRecipientModal({ open: false, docId: null, ideaIds: [], allDocIds: null });
    } catch (err) {
      console.error('Error sending to client:', err);
      const errorMessage = err.response?.data?.error || 'Failed to send ideas to client';
      modal.error(errorMessage);

      // If assignment error, refresh the ideas list to remove items user can no longer access
      if (errorMessage.includes('no longer assigned')) {
        if (allDocIds) {
          const clearedState = {};
          allDocIds.forEach(id => { clearedState[id] = []; });
          setSelectedIdeas(prev => ({ ...prev, ...clearedState }));
          setApprovedIdeas(prev => ({ ...prev, ...clearedState }));
        } else {
          setSelectedIdeas(prev => ({ ...prev, [docId]: [] }));
          setApprovedIdeas(prev => ({ ...prev, [docId]: [] }));
        }
        fetchIdeas();
      }
    } finally {
      setSendingToClient(false);
    }
  };

  // Checkbox: neutral toggle. If item is in any state, clear all. If in no state, add to neutral.
  const toggleIdeaRowSelect = (docId, ideaId) => {
    const isNeutral = (selectedIdeas[docId] || []).includes(ideaId);
    const isApproved = (approvedIdeas[docId] || []).includes(ideaId);
    const isDiscarded = (discardedIdeas[docId] || []).includes(ideaId);
    if (isNeutral || isApproved || isDiscarded) {
      setSelectedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
      setApprovedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
      setDiscardedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
    } else {
      setSelectedIdeas(prev => ({ ...prev, [docId]: [...(prev[docId] || []), ideaId] }));
    }
  };

  // ✓ Send button: stage for send. Mutually exclusive with neutral and discarded.
  const stageIdeaForSend = (docId, ideaId) => {
    const isApproved = (approvedIdeas[docId] || []).includes(ideaId);
    if (isApproved) {
      setApprovedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
    } else {
      setApprovedIdeas(prev => ({ ...prev, [docId]: [...(prev[docId] || []), ideaId] }));
      setSelectedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
      setDiscardedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
    }
  };

  // ✗ Discard button: stage for discard. Mutually exclusive with neutral and approved.
  const stageIdeaForDiscard = (docId, ideaId) => {
    const isDiscarded = (discardedIdeas[docId] || []).includes(ideaId);
    if (isDiscarded) {
      setDiscardedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
    } else {
      setDiscardedIdeas(prev => ({ ...prev, [docId]: [...(prev[docId] || []), ideaId] }));
      setSelectedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
      setApprovedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
    }
  };

  // Discard all checked/staged-for-discard ideas across every doc in a single confirm
  const handleBatchDiscard = async () => {
    const entries = Object.keys({ ...selectedIdeas, ...discardedIdeas }).map(docId => {
      const neutral = selectedIdeas[docId] || [];
      const discarded = discardedIdeas[docId] || [];
      return [docId, [...new Set([...neutral, ...discarded])]];
    }).filter(([, ids]) => ids.length > 0);
    const total = entries.reduce((sum, [, ids]) => sum + ids.length, 0);
    if (total === 0) return;
    const result = await modal.confirm(`Discard ${total} idea(s)? This cannot be undone.`, 'Confirm Discard');
    if (!result) return;
    try {
      await Promise.all(entries.flatMap(([docId, ids]) =>
        ids.map(ideaId => axios.put(`/api/ideas/${docId}/idea/${ideaId}/status`, {
          status: 'rejected_internal'
        }, { withCredentials: true }))
      ));
      updateIdeasStatusLocally(Object.fromEntries(entries), 'rejected_internal');
      setSelectedIdeas({});
      setDiscardedIdeas({});
    } catch (err) {
      console.error('Error discarding ideas:', err);
      modal.error(err.response?.data?.error || 'Failed to discard ideas');
    }
  };


  // Archive all checked ideas across every doc in a single confirm - removes them
  // from the operator's to-do list without discarding them
  const handleBatchArchive = async () => {
    const entries = Object.entries(selectedIdeas).filter(([, ids]) => ids.length > 0);
    const total = entries.reduce((sum, [, ids]) => sum + ids.length, 0);
    if (total === 0) return;
    const result = await modal.confirm(`Archive ${total} idea(s)? They'll be removed from your to-do list until restored from the Archive tab.`, 'Confirm Archive');
    if (!result) return;
    try {
      await Promise.all(entries.flatMap(([docId, ids]) =>
        ids.map(ideaId => axios.put(`/api/ideas/${docId}/idea/${ideaId}/status`, {
          status: 'archived'
        }, { withCredentials: true }))
      ));
      updateIdeasStatusLocally(Object.fromEntries(entries), 'archived', { archived_at: new Date().toISOString() });
      setSelectedIdeas({});
    } catch (err) {
      console.error('Error archiving ideas:', err);
      modal.error(err.response?.data?.error || 'Failed to archive ideas');
    }
  };

  // Checkbox toggle for the Archive tab's selection. Mirrors toggleIdeaRowSelect:
  // clicking a row that's staged in any bucket clears it entirely; otherwise it
  // just adds it to the neutral bucket.
  const toggleArchivedIdeaSelect = (docId, ideaId) => {
    const isNeutral = (selectedArchivedIdeas[docId] || []).includes(ideaId);
    const isApproved = (approvedArchivedIdeas[docId] || []).includes(ideaId);
    const isDiscarded = (discardedArchivedIdeas[docId] || []).includes(ideaId);
    if (isNeutral || isApproved || isDiscarded) {
      setSelectedArchivedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
      setApprovedArchivedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
      setDiscardedArchivedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
    } else {
      setSelectedArchivedIdeas(prev => ({ ...prev, [docId]: [...(prev[docId] || []), ideaId] }));
    }
  };

  // ✓ Restore button: stage for restore. Mutually exclusive with neutral and discarded.
  const stageArchivedIdeaForRestore = (docId, ideaId) => {
    const isApproved = (approvedArchivedIdeas[docId] || []).includes(ideaId);
    if (isApproved) {
      setApprovedArchivedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
    } else {
      setApprovedArchivedIdeas(prev => ({ ...prev, [docId]: [...(prev[docId] || []), ideaId] }));
      setSelectedArchivedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
      setDiscardedArchivedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
    }
  };

  // ✗ Discard button: stage for discard. Mutually exclusive with neutral and approved.
  const stageArchivedIdeaForDiscard = (docId, ideaId) => {
    const isDiscarded = (discardedArchivedIdeas[docId] || []).includes(ideaId);
    if (isDiscarded) {
      setDiscardedArchivedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
    } else {
      setDiscardedArchivedIdeas(prev => ({ ...prev, [docId]: [...(prev[docId] || []), ideaId] }));
      setSelectedArchivedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
      setApprovedArchivedIdeas(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(id => id !== ideaId) }));
    }
  };

  const handleBatchRestoreArchived = async () => {
    const entries = Object.keys({ ...selectedArchivedIdeas, ...approvedArchivedIdeas }).map(docId => {
      const neutral = selectedArchivedIdeas[docId] || [];
      const approved = approvedArchivedIdeas[docId] || [];
      return [docId, [...new Set([...neutral, ...approved])]];
    }).filter(([, ids]) => ids.length > 0);
    const total = entries.reduce((sum, [, ids]) => sum + ids.length, 0);
    if (total === 0) return;
    const result = await modal.confirm(`Restore ${total} idea(s) to Review?`, 'Confirm Restore');
    if (!result) return;
    try {
      await Promise.all(entries.flatMap(([docId, ids]) =>
        ids.map(ideaId => axios.put(`/api/ideas/${docId}/idea/${ideaId}/status`, {
          status: 'pending_review'
        }, { withCredentials: true }))
      ));
      updateIdeasStatusLocally(Object.fromEntries(entries), 'pending_review');
      setSelectedArchivedIdeas({});
      setApprovedArchivedIdeas({});
    } catch (err) {
      console.error('Error restoring ideas:', err);
      modal.error(err.response?.data?.error || 'Failed to restore ideas');
    }
  };

  const handleBatchDiscardArchived = async () => {
    const entries = Object.keys({ ...selectedArchivedIdeas, ...discardedArchivedIdeas }).map(docId => {
      const neutral = selectedArchivedIdeas[docId] || [];
      const discarded = discardedArchivedIdeas[docId] || [];
      return [docId, [...new Set([...neutral, ...discarded])]];
    }).filter(([, ids]) => ids.length > 0);
    const total = entries.reduce((sum, [, ids]) => sum + ids.length, 0);
    if (total === 0) return;
    const result = await modal.confirm(`Discard ${total} idea(s)? This cannot be undone.`, 'Confirm Discard');
    if (!result) return;
    try {
      await Promise.all(entries.flatMap(([docId, ids]) =>
        ids.map(ideaId => axios.put(`/api/ideas/${docId}/idea/${ideaId}/status`, {
          status: 'rejected_internal'
        }, { withCredentials: true }))
      ));
      updateIdeasStatusLocally(Object.fromEntries(entries), 'rejected_internal');
      setSelectedArchivedIdeas({});
      setDiscardedArchivedIdeas({});
    } catch (err) {
      console.error('Error discarding ideas:', err);
      modal.error(err.response?.data?.error || 'Failed to discard ideas');
    }
  };

  const selectAllIdeas = (docId, ideas) => {
    const selectableIdeas = ideas.filter(i =>
      i.status === 'pending_review' || i.status === 'revision_requested' || i.status === 'denied'
    );
    const selectableIds = selectableIdeas.map(i => i.id);
    const neutral = selectedIdeas[docId] || [];
    const approved = approvedIdeas[docId] || [];
    const discarded = discardedIdeas[docId] || [];
    const allSelected = selectableIds.length > 0 && selectableIds.every(id =>
      neutral.includes(id) || approved.includes(id) || discarded.includes(id)
    );
    if (allSelected) {
      setSelectedIdeas(prev => ({ ...prev, [docId]: [] }));
      setApprovedIdeas(prev => ({ ...prev, [docId]: [] }));
      setDiscardedIdeas(prev => ({ ...prev, [docId]: [] }));
    } else {
      setSelectedIdeas(prev => ({ ...prev, [docId]: selectableIds }));
      setApprovedIdeas(prev => ({ ...prev, [docId]: [] }));
      setDiscardedIdeas(prev => ({ ...prev, [docId]: [] }));
    }
  };

  const handleClearFlag = async () => {
    const confirmed = await modal.confirm(
      'This removes the "Needs Review" flag from this idea. Only clear it once you\'ve verified the idea is accurate.',
      'Clear Review Flag',
      { confirmText: 'Clear Flag', cancelText: 'Cancel' }
    );
    if (!confirmed) return;

    const { docId, idea } = editModal;
    try {
      setClearingFlag(true);
      await axios.put(`/api/ideas/${docId}/idea/${idea.id}/clear-flag`, {}, { withCredentials: true });

      setIdeasDocs(prevDocs => prevDocs.map(doc => {
        if (doc._id !== docId) return doc;
        return {
          ...doc,
          ideas: doc.ideas.map(i => i.id === idea.id ? { ...i, flagged: false, flag_reason: null } : i)
        };
      }));
      setEditModal(prev => prev.idea && prev.idea.id === idea.id
        ? { ...prev, idea: { ...prev.idea, flagged: false, flag_reason: null } }
        : prev
      );
    } catch (err) {
      modal.error(err.response?.data?.error || 'Failed to clear review flag');
    } finally {
      setClearingFlag(false);
    }
  };

  // Open edit modal for an idea
  const openEditModal = (docId, idea) => {
    const initialForm = {
      title: idea.title || '',
      synopsis: idea.synopsis || idea.description || '',
      content_type: idea.content_type || 'social',
      length: idea.length || 'medium',
      key_points: idea.key_points || [],
      sources: idea.sources || [],
      additional_source_ids: idea.additional_source_ids || []
    };
    setEditForm(initialForm);
    setEditFormOriginal(initialForm);
    setSourceViewer({ open: false, loading: false, source: null, copied: false });
    setEditModal({ open: true, docId, idea });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, docId: null, idea: null });
    setEditFormOriginal(null);
    setSourceViewer({ open: false, loading: false, source: null, copied: false });
  };

  // Only the fields actually sent to the server on save are compared - "sources"
  // is display-only metadata, never submitted.
  const EDIT_FORM_COMPARE_KEYS = ['title', 'synopsis', 'content_type', 'length', 'key_points', 'additional_source_ids'];
  const isEditFormUnchanged = () => {
    if (!editFormOriginal) return true;
    return EDIT_FORM_COMPARE_KEYS.every(
      key => JSON.stringify(editForm[key]) === JSON.stringify(editFormOriginal[key])
    );
  };

  // Save edited idea
  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) {
      modal.warning('Title is required');
      return;
    }

    try {
      setSaving(true);
      await axios.put(
        `/api/ideas/${editModal.docId}/idea/${editModal.idea.id}/content`,
        editForm,
        { withCredentials: true }
      );

      // Update the local state instead of refreshing
      setIdeasDocs(prevDocs => {
        return prevDocs.map(doc => {
          if (doc._id === editModal.docId) {
            return {
              ...doc,
              ideas: doc.ideas.map(idea => {
                if (idea.id === editModal.idea.id) {
                  const updatedIdea = {
                    ...idea,
                    title: editForm.title,
                    synopsis: editForm.synopsis,
                    content_type: editForm.content_type,
                    length: editForm.length,
                    key_points: editForm.key_points,
                    sources: editForm.sources,
                    additional_source_ids: editForm.additional_source_ids
                  };
                  // If editing a revision_requested item, mark it as revised
                  if (idea.status === 'revision_requested') {
                    updatedIdea.has_been_revised = true;
                  }
                  return updatedIdea;
                }
                return idea;
              })
            };
          }
          return doc;
        });
      });

      closeEditModal();
    } catch (err) {
      console.error('Error saving idea:', err);
      modal.error(err.response?.data?.error || 'Failed to save idea');
    } finally {
      setSaving(false);
    }
  };


  // Handle key points editing
  const updateKeyPoint = (index, value) => {
    setEditForm(prev => {
      const newPoints = [...prev.key_points];
      newPoints[index] = value;
      return { ...prev, key_points: newPoints };
    });
  };

  const addKeyPoint = () => {
    setEditForm(prev => ({
      ...prev,
      key_points: [...prev.key_points, '']
    }));
  };

  const removeKeyPoint = (index) => {
    setEditForm(prev => ({
      ...prev,
      key_points: prev.key_points.filter((_, i) => i !== index)
    }));
  };

  // Handle attaching additional sources to an idea
  const openSourcePicker = async () => {
    const doc = ideasDocs.find(d => d._id === editModal.docId);
    const brandId = doc?.brand_id;
    if (!brandId) {
      modal.warning('Could not determine brand for this idea');
      return;
    }
    setSourcePicker({ open: true, loading: true, available: [], selectedIds: [] });
    try {
      const response = await axios.get('/api/sources', {
        params: { brand_id: brandId },
        withCredentials: true
      });
      const attachedIds = new Set(editForm.sources.map(s => s._id));
      const available = (response.data.sources || []).filter(s => !attachedIds.has(s._id));
      setSourcePicker({ open: true, loading: false, available, selectedIds: [] });
    } catch (err) {
      console.error('Error fetching sources:', err);
      modal.error('Failed to load sources');
      setSourcePicker({ open: false, loading: false, available: [], selectedIds: [] });
    }
  };

  const toggleSourcePickerSelect = (sourceId) => {
    setSourcePicker(prev => ({
      ...prev,
      selectedIds: prev.selectedIds.includes(sourceId)
        ? prev.selectedIds.filter(id => id !== sourceId)
        : [...prev.selectedIds, sourceId]
    }));
  };

  const confirmAddSources = () => {
    const sourcesToAdd = sourcePicker.available.filter(s => sourcePicker.selectedIds.includes(s._id));
    setEditForm(prev => ({
      ...prev,
      sources: [...prev.sources, ...sourcesToAdd],
      additional_source_ids: [...prev.additional_source_ids, ...sourcesToAdd.map(s => s._id)]
    }));
    setSourcePicker({ open: false, loading: false, available: [], selectedIds: [] });
  };

  const closeSourcePicker = () => {
    setSourcePicker({ open: false, loading: false, available: [], selectedIds: [] });
  };

  const removeAddedSource = (sourceId) => {
    setEditForm(prev => ({
      ...prev,
      sources: prev.sources.filter(s => s._id !== sourceId),
      additional_source_ids: prev.additional_source_ids.filter(id => id !== sourceId)
    }));
  };

  // Open the side-by-side source viewer pane within the Edit Idea modal.
  // The idea's attached sources only carry name/type/date (no content, to keep
  // the ideas list payload small), so fetch the full source doc on demand.
  const viewIdeaSource = async (sourceStub) => {
    // Clicking the view button for the source that's already open toggles it closed
    if (sourceViewer.open && sourceViewer.source && sourceViewer.source._id === sourceStub._id) {
      closeSourceViewer();
      return;
    }
    setSourceViewer({ open: true, loading: true, source: null, copied: false });
    try {
      const doc = ideasDocs.find(d => d._id === editModal.docId);
      const brandId = doc?.brand_id;
      const response = await axios.get('/api/sources', {
        params: { brand_id: brandId },
        withCredentials: true
      });
      const fullSource = (response.data.sources || []).find(s => s._id === sourceStub._id);
      setSourceViewer({ open: true, loading: false, source: fullSource || sourceStub, copied: false });
    } catch (err) {
      console.error('Error loading source content:', err);
      setSourceViewer({ open: true, loading: false, source: sourceStub, copied: false });
    }
  };

  const closeSourceViewer = () => {
    setSourceViewer({ open: false, loading: false, source: null, copied: false });
  };

  const copyIdeaSourceContent = async () => {
    const source = sourceViewer.source;
    if (!source) return;

    let contentToCopy = '';
    if (source.source_type === 'url') {
      contentToCopy = source.original_url || source.content || '';
    } else if (typeof source.content === 'object' && source.content !== null) {
      contentToCopy = source.content.formatted || source.content.simple || JSON.stringify(source.content, null, 2);
    } else {
      contentToCopy = source.content || '';
    }

    if (contentToCopy) {
      try {
        await navigator.clipboard.writeText(contentToCopy);
        setSourceViewer(prev => ({ ...prev, copied: true }));
        setTimeout(() => setSourceViewer(prev => ({ ...prev, copied: false })), 2000);
      } catch (err) {
        console.error('Error copying to clipboard:', err);
      }
    }
  };

  const getSourceIcon = (type) => {
    const iconMap = {
      url: 'link',
      text: 'doc',
      image: 'picture',
      video: 'play',
      audio: 'play',
      transcription: 'comment-empty',
      squadcast_transcript: 'comment-empty',
      meeting: 'comment-empty',
      pdf: 'doc-text',
      google_drive: 'google',
      brand_profile: 'user'
    };
    return iconMap[type] || 'doc';
  };

  // Renders a source's content inside the side-by-side viewer pane
  const renderSourceContent = (source) => {
    if (source.source_type === 'url') {
      return (
        <div className="source-pane-section">
          <div className="source-pane-url-display">
            <Icon name="link" size="1.1rem" />
            <a
              href={source.original_url || source.content}
              target="_blank"
              rel="noopener noreferrer"
              className="source-pane-url-value"
            >
              {source.original_url || source.content}
            </a>
          </div>
          {source.content && source.content !== source.original_url && (
            <div>
              <h5 className="source-pane-subheading">Scraped Content</h5>
              <pre className="source-pane-content-text">{source.content}</pre>
            </div>
          )}
        </div>
      );
    }

    if (source.source_type === 'meeting' || source.source_type === 'transcription' || source.source_type === 'squadcast_transcript') {
      const content = source.content;
      if (!content) {
        return <p className="source-pane-empty">No transcript available.</p>;
      }
      const textContent = typeof content === 'object' && content !== null
        ? (content.formatted || content.simple || JSON.stringify(content, null, 2))
        : content;
      return <pre className="source-pane-content-text source-pane-transcript-text">{textContent}</pre>;
    }

    if (source.source_type === 'brand_profile') {
      const content = source.content;
      if (!content) {
        return <p className="source-pane-empty">No brand profile data available.</p>;
      }
      let profileData;
      try {
        profileData = typeof content === 'string' ? JSON.parse(content) : content;
      } catch (e) {
        return <pre className="source-pane-content-text">{content}</pre>;
      }

      const renderSection = (title, value, isList = false) => {
        if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && !value.trim())) {
          return null;
        }
        return (
          <div className="source-pane-profile-section" key={title}>
            <h5 className="source-pane-subheading">{title}</h5>
            {isList ? (
              <ul className="source-pane-profile-list">
                {value.map((item, idx) => <li key={idx}>{item}</li>)}
              </ul>
            ) : (
              <p className="source-pane-profile-text">{value}</p>
            )}
          </div>
        );
      };

      return (
        <div className="source-pane-section">
          {renderSection('Target Audience', profileData.audience)}
          {renderSection('Mission & Core Values', profileData.mission_core_values)}
          {renderSection('Value Propositions', profileData.value_propositions, true)}
          {renderSection('Key Differentiators', profileData.differentiators, true)}
          {renderSection('Products & Services', profileData.products_services)}
          {renderSection('Locations', profileData.locations, true)}
          {renderSection('Ideal Clients', profileData.ideal_clients)}
          {renderSection('Industry Details', profileData.industry_details)}
          {renderSection('Competitors', profileData.competitors, true)}
          {renderSection('Offers', profileData.offers, true)}
        </div>
      );
    }

    return source.content
      ? <pre className="source-pane-content-text">{source.content}</pre>
      : <p className="source-pane-empty">No content available.</p>;
  };

  const getContentTypeBadge = (contentType) => {
    if (!contentType) {
      return <span style={{ color: 'var(--color-text-disabled)', fontSize: '0.85rem' }}>None</span>;
    }

    const typeColors = {
      'social': 'var(--color-content-type-social)',
      'blog': 'var(--color-content-type-blog)',
      'video': 'var(--color-content-type-video)',
      'podcast': 'var(--color-content-type-podcast)',
      'newsletter': 'var(--color-content-type-newsletter)'
    };

    const typeLabels = {
      'social': 'Social',
      'blog': 'Blog',
      'video': 'Video',
      'podcast': 'Podcast',
      'newsletter': 'Newsletter'
    };

    return (
      <span
        className="content-type-badge"
        style={{
          backgroundColor: typeColors[contentType.toLowerCase()] || 'var(--color-content-type-default)',
          fontSize: '0.75rem',
          padding: '2px 8px'
        }}
      >
        {typeLabels[contentType.toLowerCase()] || contentType}
      </span>
    );
  };

  const getSourceOriginBadge = (idea) => {
    if (idea.sourceType === 'sources' && idea.sourceIds && idea.sourceIds.length > 0) {
      return (
        <span className="idea-origin-badge idea-origin-library" title={`Generated from ${idea.sourceIds.length} source(s)`}>
          From Source Library
        </span>
      );
    }
    if (idea.sourceType === 'manual') {
      return <span className="idea-origin-badge idea-origin-manual">From Manual Creation</span>;
    }
    return <span className="idea-origin-badge idea-origin-automated">From Automated Workflow</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSourceTypeName = (source) => {
    const type = source.source_type;
    const metadata = source.metadata || {};
    if ((type === 'transcription' || type === 'meeting') && (metadata.is_squadcast || metadata.squadcast_session_id)) {
      return 'SQUADCAST TRANSCRIPT';
    }
    const typeMap = {
      meeting: 'TRANSCRIPT',
      transcription: 'TRANSCRIPT',
      squadcast_transcript: 'SQUADCAST TRANSCRIPT',
      brand_profile: 'BRAND PROFILE',
      url: 'URL',
      text: 'TEXT',
      image: 'IMAGE',
      video: 'VIDEO',
      audio: 'AUDIO',
      pdf: 'PDF',
      google_drive: 'GOOGLE DRIVE'
    };
    return typeMap[type] || (type ? type.toUpperCase() : 'SOURCE');
  };

  const formatCommentTimestamp = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${month}/${day} ${hours}:${minutesStr} ${ampm}`;
  };

  const filterIdeas = (ideas) => {
    // Only show ideas still in operator workflow.
    // 'sent_to_client' and 'approved' are no longer operator-editable.
    // 'approved_internal' and 'rejected_internal' have been processed and should be in history.
    // 'archived' has been pulled off the to-do list and lives in the Archive tab.
    // 'denied' and 'revision_requested' come back from the client and need operator attention.
    const agentWorkflowIdeas = ideas.filter(idea =>
      idea.status !== 'sent_to_client' &&
      idea.status !== 'approved' &&
      idea.status !== 'approved_internal' &&
      idea.status !== 'rejected_internal' &&
      idea.status !== 'archived'
    );

    if (filterStatus === 'all') return agentWorkflowIdeas;
    return agentWorkflowIdeas.filter(idea => idea.status === filterStatus);
  };

  const filterArchivedIdeas = (ideas) => ideas.filter(idea => idea.status === 'archived');

  const filterHistoryIdeas = (ideas) => {
    // Only show ideas that have been acted upon
    // For operators: sent_to_client is an action they took
    // For clients: sent_to_client means pending (waiting for their action)
    if (user?.is_operator) {
      return ideas.filter(idea =>
        ['sent_to_client', 'approved', 'denied', 'revision_requested', 'approved_internal', 'rejected_internal'].includes(idea.status)
      );
    } else {
      // Clients only see items where THEY took action
      return ideas.filter(idea =>
        ['approved', 'denied', 'revision_requested'].includes(idea.status)
      );
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending_review': { label: 'Ready to Edit', color: 'var(--color-idea-status-ready-to-edit)', icon: null },
      'approved_internal': { label: 'Approved Internal', color: 'var(--color-idea-status-approved-internal)', icon: null },
      'rejected_internal': { label: 'Discarded', color: 'var(--color-error)', icon: null },
      'sent_to_client': { label: 'Sent to Client', color: 'var(--color-idea-status-sent-to-client)', icon: null },
      'approved': { label: 'Client Approved', color: 'var(--color-idea-status-client-approved)', icon: null },
      'denied': { label: 'Client Denied', color: 'var(--color-error)', icon: null },
      'revision_requested': { label: 'Revision Requested', color: 'var(--color-idea-status-revision-requested)', icon: null },
      'archived': { label: 'Archived', color: 'var(--color-idea-status-archived)', icon: null }
    };
    const config = statusConfig[status] || { label: status, color: 'var(--color-idea-status-default)', icon: null };
    return (
      <span className="status-badge" style={{ backgroundColor: config.color }}>
        {config.icon && <Icon name={config.icon} size="0.875rem" />}
        {config.label}
      </span>
    );
  };

  const renderEmptyState = () => (
    <div className="empty-state-ideas">
      <div className="empty-icon"><Icon name="lightbulb" size="3rem" /></div>
      <h3>No Ideas Yet</h3>
      <p>Ideas will appear here after you generate them from meeting transcripts.</p>
    </div>
  );

  if (loading && ideasDocs.length === 0) {
    return (
      <div className="ideas-panel-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading ideas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ideas-panel-container">
      <div className="ideas-content-wrapper">
        {/* Tabs */}
        <div className="ideas-tabs-container">
          <button
            className={`ideas-tab ${activeTab === 'current' ? 'active' : ''}`}
            onClick={() => setActiveTab('current')}
          >
            Review
          </button>
          <button
            className={`ideas-tab ${activeTab === 'archive' ? 'active' : ''}`}
            onClick={() => setActiveTab('archive')}
          >
            Archive
          </button>
          <button
            className={`ideas-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
        </div>

        <MonthlyProgressTracker brandId={brandId} />

        {activeTab === 'history' ? (
          <div className="history-section">
            <div className="ideas-header-controls">
              {activityLogs.length > 0 && (
                <div className="pagination-info">
                  Showing {Math.min((historyPage - 1) * HISTORY_ITEMS_PER_PAGE + 1, sortedActivityLogs.length)}-{Math.min(historyPage * HISTORY_ITEMS_PER_PAGE, sortedActivityLogs.length)} of {sortedActivityLogs.length} items
                </div>
              )}
              <div className="header-actions">
                <button className="btn-refresh" onClick={fetchActivityLogs}>
                  Refresh
                </button>
              </div>
            </div>

            {activityLogs.length === 0 ? (
              <div className="empty-state-ideas">
                <div className="empty-icon"><Icon name="lightbulb" size="3rem" /></div>
                <h3>No Ideas History Yet</h3>
                <p>Approved, denied, and archived ideas will show up here once there's activity.</p>
              </div>
            ) : (
              (() => {
                // Calculate pagination (sort first, then paginate)
                const totalPages = Math.ceil(sortedActivityLogs.length / HISTORY_ITEMS_PER_PAGE);
                const startIndex = (historyPage - 1) * HISTORY_ITEMS_PER_PAGE;
                const endIndex = startIndex + HISTORY_ITEMS_PER_PAGE;
                const paginatedLogs = sortedActivityLogs.slice(startIndex, endIndex);

                return (
                  <div className="ideas-list">
                    <div className="ideas-doc-card">
                      <div className="ideas-doc-content">
                        <div className="ideas-table-wrapper">
                          <table className="ideas-table">
                            <thead>
                              <tr>
                                <th style={{ width: '80px' }} className={getHistorySortClass('brandName')} onClick={() => requestHistorySort('brandName')}>Brand</th>
                                <th className={getHistorySortClass('title')} onClick={() => requestHistorySort('title')}>Ideas</th>
                                <th style={{ width: '120px' }} className={getHistorySortClass('actionDate')} onClick={() => requestHistorySort('actionDate')}>Action Date</th>
                                <th style={{ width: '80px' }}>Comments</th>
                                <th style={{ width: '120px' }}>Type</th>
                                <th style={{ width: '180px' }} className={getHistorySortClass('status')} onClick={() => requestHistorySort('status')}>Current Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedLogs.map((idea) => {
                            const synopsis = idea.synopsis;
                            const actionDate = idea.actionDate;

                            return (
                              <tr key={idea._id} className="idea-row">
                                <td className="brand-cell">
                                  {idea.brandLogo ? (
                                    <BrandIcon src={idea.brandLogo} alt={idea.brandName} className="brand-logo-small" />
                                  ) : (
                                    <div className="brand-placeholder-small">
                                      <span>{(idea.brandName || 'B').charAt(0).toUpperCase()}</span>
                                    </div>
                                  )}
                                </td>
                                <td className="idea-content-cell">
                                  <div className="idea-content">
                                    <div className="idea-title-row">
                                      <strong>{idea.title}</strong>
                                      {idea.flagged && (
                                        <span className="flagged-inline-badge">
                                          <Icon name="flag" size="0.85rem" /> Needs Review
                                        </span>
                                      )}
                                    </div>
                                    <div className="idea-synopsis">{synopsis}</div>
                                  </div>
                                </td>
                                <td className="date-cell">
                                  {formatDate(actionDate)}
                                </td>
                                <td className="comment-cell">
                                  <button
                                    className="comment-icon-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setCommentModalState({
                                        open: true,
                                        docId: idea.docId,
                                        ideaId: idea.id,
                                        comments: idea.comments || []
                                      });
                                    }}
                                    title="Add comment"
                                    type="button"
                                  >
                                    <Icon name="comment-empty" size="1.5rem" />
                                    {idea.comments && idea.comments.length > 0 && (
                                      <span className="comment-count-badge">{idea.comments.length}</span>
                                    )}
                                  </button>
                                </td>
                                <td className="tags-cell">
                                  {getContentTypeBadge(idea.content_type)}
                                </td>
                                <td className="status-cell">
                                  {getStatusBadge(idea.status)}
                                </td>
                              </tr>
                            );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                          <div className="pagination-controls">
                            <button
                              className="pagination-btn"
                              onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                              disabled={historyPage === 1}
                            >
                              <Icon name="arrow-left" size="1rem" />
                              Previous
                            </button>
                            <div className="pagination-numbers">
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (historyPage <= 3) {
                                  pageNum = i + 1;
                                } else if (historyPage >= totalPages - 2) {
                                  pageNum = totalPages - 4 + i;
                                } else {
                                  pageNum = historyPage - 2 + i;
                                }
                                return (
                                  <button
                                    key={pageNum}
                                    className={`pagination-number ${historyPage === pageNum ? 'active' : ''}`}
                                    onClick={() => setHistoryPage(pageNum)}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}
                            </div>
                            <button
                              className="pagination-btn"
                              onClick={() => setHistoryPage(prev => Math.min(totalPages, prev + 1))}
                              disabled={historyPage === totalPages}
                            >
                              Next
                              <Icon name="arrow-right" size="1rem" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        ) : activeTab === 'archive' ? (() => {
            const allArchivedIdeasRaw = [];
            ideasDocs.forEach(doc => {
              const archivedIdeas = filterArchivedIdeas(doc.ideas || []);
              archivedIdeas.forEach(idea => {
                allArchivedIdeasRaw.push({
                  ...idea,
                  docId: doc._id,
                  brandName: doc.brand?.name || doc.brand?.brand_name || 'Unknown',
                  brandLogo: doc.brand?.icon_url,
                  generatedAt: doc.generated_at,
                  sourceType: doc.source_type,
                  sourceIds: doc.source_ids,
                  actionDate: idea.archived_at || idea.updated_at || doc.generated_at
                });
              });
            });
            const allArchivedIdeas = sortData(allArchivedIdeasRaw, archiveSortConfig);
            const requestArchiveSort = (key) => setArchiveSortConfig(prev =>
              prev?.key === key && prev.direction === 'asc' ? { key, direction: 'desc' } : { key, direction: 'asc' }
            );
            const getArchiveSortClass = (key) => archiveSortConfig?.key === key ? `sortable-th sort-${archiveSortConfig.direction}` : 'sortable-th';

            const allArchivedNeutral = Object.values(selectedArchivedIdeas).flat();
            const allArchivedApproved = Object.values(approvedArchivedIdeas).flat();
            const allArchivedDiscarded = Object.values(discardedArchivedIdeas).flat();
            const totalRestoreCount = new Set([...allArchivedNeutral, ...allArchivedApproved]).size;
            const totalArchiveDiscardCount = new Set([...allArchivedNeutral, ...allArchivedDiscarded]).size;

            const allArchivedSelected = allArchivedIdeas.length > 0 && allArchivedIdeas.every(i =>
              allArchivedNeutral.includes(i.id) || allArchivedApproved.includes(i.id) || allArchivedDiscarded.includes(i.id)
            );

            return (
              <div className="archive-section">
                <div className="ideas-header-controls">
                  <div className="ideas-select-batch-group">
                    <label className="select-all-checkbox">
                      <input
                        type="checkbox"
                        checked={allArchivedSelected}
                        onChange={() => {
                          if (allArchivedSelected) {
                            setSelectedArchivedIdeas({});
                            setApprovedArchivedIdeas({});
                            setDiscardedArchivedIdeas({});
                          } else {
                            const newSelected = {};
                            allArchivedIdeas.forEach(i => {
                              if (!newSelected[i.docId]) newSelected[i.docId] = [];
                              newSelected[i.docId].push(i.id);
                            });
                            setSelectedArchivedIdeas(newSelected);
                            setApprovedArchivedIdeas({});
                            setDiscardedArchivedIdeas({});
                          }
                        }}
                      />
                      Select All
                    </label>
                    <div className="batch-action-buttons">
                      <button
                        className={`btn-batch-restore${totalRestoreCount === 0 ? ' btn-batch-inactive' : ''}`}
                        onClick={handleBatchRestoreArchived}
                        disabled={totalRestoreCount === 0}
                      >
                        Restore{totalRestoreCount > 0 ? ` ${totalRestoreCount}` : ''}
                      </button>
                      <button
                        className={`btn-batch-discard${totalArchiveDiscardCount === 0 ? ' btn-batch-inactive' : ''}`}
                        onClick={handleBatchDiscardArchived}
                        disabled={totalArchiveDiscardCount === 0}
                      >
                        Discard{totalArchiveDiscardCount > 0 ? ` ${totalArchiveDiscardCount}` : ''}
                      </button>
                    </div>
                  </div>
                  <div className="header-actions">
                    <button className="btn-refresh" onClick={fetchIdeas}>
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="ideas-list">
                  {allArchivedIdeas.length === 0 ? (
                    <div className="empty-state-ideas">
                      <div className="empty-icon"><Icon name="lightbulb" size="3rem" /></div>
                      <h3>No Archived Ideas</h3>
                      <p>Ideas you archive will be saved here for future reference.</p>
                    </div>
                  ) : (
                    <div className="ideas-doc-card">
                      <div className="ideas-doc-content">
                        <div className="ideas-table-wrapper">
                        <table className="ideas-table">
                          <thead>
                            <tr>
                              <th style={{ width: '40px' }}></th>
                              <th style={{ width: '80px' }} className={getArchiveSortClass('brandName')} onClick={() => requestArchiveSort('brandName')}>Brand</th>
                              <th className={getArchiveSortClass('title')} onClick={() => requestArchiveSort('title')}>Ideas</th>
                              <th style={{ width: '210px' }}>Source</th>
                              <th style={{ width: '120px' }} className={getArchiveSortClass('actionDate')} onClick={() => requestArchiveSort('actionDate')}>Archived Date</th>
                              <th style={{ width: '80px' }}>Comments</th>
                              <th style={{ width: '120px' }}>Type</th>
                              <th style={{ width: '190px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allArchivedIdeas.map((idea) => {
                              const synopsis = idea.synopsis || idea.description;
                              const isArchivedNeutral = (selectedArchivedIdeas[idea.docId] || []).includes(idea.id);
                              const isArchivedApproved = (approvedArchivedIdeas[idea.docId] || []).includes(idea.id);
                              const isArchivedDiscarded = (discardedArchivedIdeas[idea.docId] || []).includes(idea.id);
                              const isAnyArchivedStaged = isArchivedNeutral || isArchivedApproved || isArchivedDiscarded;

                              return (
                                <tr key={`${idea.docId}-${idea.id}`} className={`idea-row${isArchivedNeutral ? ' neutral-selected' : ''}${isArchivedApproved ? ' selected' : ''}${isArchivedDiscarded ? ' discard-staged' : ''}`}>
                                  <td className="checkbox-cell">
                                    <input
                                      type="checkbox"
                                      checked={isAnyArchivedStaged}
                                      onChange={() => toggleArchivedIdeaSelect(idea.docId, idea.id)}
                                      className="idea-checkbox"
                                    />
                                  </td>
                                  <td className="brand-cell">
                                    {idea.brandLogo ? (
                                      <BrandIcon src={idea.brandLogo} alt={idea.brandName} className="brand-logo-small" />
                                    ) : (
                                      <div className="brand-placeholder-small">
                                        <span>{idea.brandName.charAt(0).toUpperCase()}</span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="idea-content-cell">
                                    <div className="idea-content">
                                      <div className="idea-title-row">
                                        <strong>{idea.title}</strong>
                                        {idea.flagged && (
                                          <span className="flagged-inline-badge">
                                            <Icon name="flag" size="0.85rem" /> Needs Review
                                          </span>
                                        )}
                                      </div>
                                      <div className="idea-synopsis">{synopsis}</div>
                                    </div>
                                  </td>
                                  <td className="source-cell">
                                    {getSourceOriginBadge(idea)}
                                  </td>
                                  <td className="date-cell">
                                    {formatDate(idea.actionDate)}
                                  </td>
                                  <td className="comment-cell">
                                    <button
                                      className="comment-icon-btn"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setCommentModalState({
                                          open: true,
                                          docId: idea.docId,
                                          ideaId: idea.id,
                                          comments: idea.comments || []
                                        });
                                      }}
                                      title="Add comment"
                                      type="button"
                                    >
                                      <Icon name="comment-empty" size="1.5rem" />
                                      {idea.comments && idea.comments.length > 0 && (
                                        <span className="comment-count-badge">{idea.comments.length}</span>
                                      )}
                                    </button>
                                  </td>
                                  <td className="tags-cell">
                                    {getContentTypeBadge(idea.content_type)}
                                  </td>
                                  <td className="actions-cell">
                                    <div className="idea-actions">
                                      <button
                                        className={`approve-btn restore ${isArchivedApproved ? 'batched' : ''}`}
                                        onClick={() => stageArchivedIdeaForRestore(idea.docId, idea.id)}
                                        title={isArchivedApproved ? 'Remove from restore batch' : 'Add to restore batch'}
                                      >
                                        <Icon name="ok" size="1.25rem" />
                                      </button>
                                      <button
                                        className="approve-btn edit"
                                        onClick={() => openEditModal(idea.docId, idea)}
                                        title="Edit idea"
                                      >
                                        <Icon name="pencil" size="1.25rem" />
                                      </button>
                                      <button
                                        className={`approve-btn discard ${isArchivedDiscarded ? 'batched-discard' : ''}`}
                                        onClick={() => stageArchivedIdeaForDiscard(idea.docId, idea.id)}
                                        title={isArchivedDiscarded ? 'Remove from discard batch' : 'Add to discard batch'}
                                      >
                                        <Icon name="cancel" size="1.25rem" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()
        : ideasDocs.length === 0 ? (
          <>
            <div className="ideas-header-controls">
              <div className="header-actions">
                <button className="btn-refresh" onClick={fetchIdeas}>
                  Refresh
                </button>
              </div>
            </div>
            {renderEmptyState()}
          </>
        ) : (() => {
            // Flatten all ideas from all docs into a single list
            const allFilteredIdeasRaw = [];
            ideasDocs.forEach(doc => {
              const filteredIdeas = filterIdeas(doc.ideas || []);
              filteredIdeas.forEach(idea => {
                allFilteredIdeasRaw.push({
                  ...idea,
                  docId: doc._id,
                  brandName: doc.brand?.name || doc.brand?.brand_name || 'Unknown',
                  brandLogo: doc.brand?.icon_url,
                  generatedAt: doc.generated_at,
                  sourceType: doc.source_type,
                  sourceIds: doc.source_ids,
                  actionDate: idea.responded_at || idea.client_decision_at || idea.sent_to_client_at || idea.updated_at || doc.generated_at
                });
              });
            });
            const allFilteredIdeas = sortData(allFilteredIdeasRaw, opIdeasSortConfig);
            const requestOpSort = (key) => setOpIdeasSortConfig(prev =>
              prev?.key === key && prev.direction === 'asc' ? { key, direction: 'desc' } : { key, direction: 'asc' }
            );
            const getOpSortClass = (key) => opIdeasSortConfig?.key === key ? `sortable-th sort-${opIdeasSortConfig.direction}` : 'sortable-th';

            // Calculate total send/discard counts across all docs
            const allNeutral = Object.values(selectedIdeas).flat();
            const allApproved = Object.values(approvedIdeas).flat();
            const allDiscarded = Object.values(discardedIdeas).flat();
            const totalSendCount = new Set([...allNeutral, ...allApproved]).size;
            const totalDiscardCount = new Set([...allNeutral, ...allDiscarded]).size;
            const totalArchiveCount = new Set(allNeutral).size;

            // Check if all selectable ideas are selected
            const selectableIdeas = allFilteredIdeas.filter(i =>
              i.status === 'pending_review' || i.status === 'revision_requested' || i.status === 'denied'
            );
            const allSelected = selectableIdeas.length > 0 && selectableIdeas.every(i =>
              allNeutral.includes(i.id) || allApproved.includes(i.id) || allDiscarded.includes(i.id)
            );

            return (
              <>
                <div className="ideas-header-controls">
                  <div className="ideas-select-batch-group">
                    <label className="select-all-checkbox">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => {
                          if (allSelected) {
                            // Deselect all
                            setSelectedIdeas({});
                            setApprovedIdeas({});
                            setDiscardedIdeas({});
                          } else {
                            // Select all selectable ideas, grouped by docId
                            const newSelected = {};
                            selectableIdeas.forEach(i => {
                              if (!newSelected[i.docId]) newSelected[i.docId] = [];
                              newSelected[i.docId].push(i.id);
                            });
                            setSelectedIdeas(newSelected);
                            setApprovedIdeas({});
                            setDiscardedIdeas({});
                          }
                        }}
                      />
                      Select All
                    </label>
                    <div className="batch-action-buttons">
                      <button
                        className={`btn-send-to-client${totalSendCount === 0 ? ' btn-batch-inactive' : ''}`}
                        onClick={() => {
                          const docIds = Object.keys({ ...selectedIdeas, ...approvedIdeas }).filter(docId => {
                            const neutral = selectedIdeas[docId] || [];
                            const approved = approvedIdeas[docId] || [];
                            const ids = [...new Set([...neutral, ...approved])];
                            return ids.length > 0;
                          });

                          if (docIds.length === 0) {
                            modal.warning('Please select at least one idea to send');
                            return;
                          }

                          if (docIds.length === 1) {
                            handleSendToClient(docIds[0]);
                          } else if (docIds.every(isGreenLightDoc)) {
                            handleRecipientsSelected([], { docId: docIds[0], ideaIds: null, allDocIds: docIds });
                          } else {
                            setRecipientModal({
                              open: true,
                              docId: docIds[0],
                              ideaIds: null,
                              allDocIds: docIds
                            });
                          }
                        }}
                        disabled={sendingToClient || totalSendCount === 0}
                      >
                        {sendingToClient ? 'Sending...' : `Send${totalSendCount > 0 ? ` ${totalSendCount}` : ''} to Client`}
                      </button>
                      <button
                        className={`btn-batch-archive${totalArchiveCount === 0 ? ' btn-batch-inactive' : ''}`}
                        onClick={handleBatchArchive}
                        disabled={totalArchiveCount === 0}
                      >
                        Archive{totalArchiveCount > 0 ? ` ${totalArchiveCount}` : ''}
                      </button>
                      <button
                        className={`btn-batch-discard${totalDiscardCount === 0 ? ' btn-batch-inactive' : ''}`}
                        onClick={handleBatchDiscard}
                        disabled={totalDiscardCount === 0}
                      >
                        Discard{totalDiscardCount > 0 ? ` ${totalDiscardCount}` : ''}
                      </button>
                    </div>
                  </div>
                  <div className="header-actions">
                      <CustomSelect
                        className="filter-status"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending_review">Ready to Edit</option>
                        <option value="revision_requested">Revision Requested</option>
                        <option value="denied">Client Denied</option>
                      </CustomSelect>
                    <button className="btn-refresh" onClick={fetchIdeas}>
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="ideas-list">
                  {allFilteredIdeas.length === 0 ? (
                    <div className="ideas-empty">
                      <p>No ideas matching this filter</p>
                    </div>
                  ) : (
                    <div className="ideas-doc-card">
                      <div className="ideas-doc-content">
                        <div className="ideas-table-wrapper">
                          <table className="ideas-table">
                            <thead>
                              <tr>
                                <th style={{ width: '40px' }}></th>
                                <th style={{ width: '80px' }} className={getOpSortClass('brandName')} onClick={() => requestOpSort('brandName')}>Brand</th>
                                <th className={getOpSortClass('title')} onClick={() => requestOpSort('title')}>Ideas</th>
                                <th style={{ width: '210px' }}>Source</th>
                                <th style={{ width: '120px' }} className={getOpSortClass('actionDate')} onClick={() => requestOpSort('actionDate')}>Action Date</th>
                                <th style={{ width: '80px' }}>Comments</th>
                                <th style={{ width: '120px' }}>Type</th>
                                <th style={{ width: '180px' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {allFilteredIdeas.map((idea) => {
                                const isNeutral = (selectedIdeas[idea.docId] || []).includes(idea.id);
                                const isApprovedIdea = (approvedIdeas[idea.docId] || []).includes(idea.id);
                                const isDiscardedIdea = (discardedIdeas[idea.docId] || []).includes(idea.id);
                                const isAnyStaged = isNeutral || isApprovedIdea || isDiscardedIdea;
                                const canSelect = idea.status === 'pending_review' || idea.status === 'revision_requested' || idea.status === 'denied';
                                const synopsis = idea.synopsis || idea.description;

                                return (
                                  <tr key={`${idea.docId}-${idea.id}`} className={`idea-row${isNeutral ? ' neutral-selected' : ''}${isApprovedIdea ? ' selected' : ''}${isDiscardedIdea ? ' discard-staged' : ''}${idea.status === 'revision_requested' && !idea.has_been_revised ? ' revision-row' : ''}${idea.status === 'revision_requested' && idea.has_been_revised ? ' revised-row' : ''}${idea.status === 'denied' ? ' denied-row' : ''}`}>
                                    <td className="checkbox-cell">
                                      {canSelect && (
                                        <input
                                          type="checkbox"
                                          checked={isAnyStaged}
                                          onChange={() => toggleIdeaRowSelect(idea.docId, idea.id)}
                                          className="idea-checkbox"
                                        />
                                      )}
                                    </td>
                                    <td className="brand-cell">
                                      {idea.brandLogo ? (
                                        <BrandIcon src={idea.brandLogo} alt={idea.brandName} className="brand-logo-small" />
                                      ) : (
                                        <div className="brand-placeholder-small">
                                          <span>{idea.brandName.charAt(0).toUpperCase()}</span>
                                        </div>
                                      )}
                                    </td>
                                    <td className="idea-content-cell">
                                      <div className="idea-content">
                                        <div className="idea-title-row">
                                          <strong>{idea.title}</strong>
                                          {idea.flagged && (
                                            <span className="flagged-inline-badge">
                                              <Icon name="flag" size="0.85rem" /> Needs Review
                                            </span>
                                          )}
                                          {idea.status === 'revision_requested' && !idea.has_been_revised && (
                                            <span className="revision-inline-badge">Revision Requested</span>
                                          )}
                                          {idea.status === 'revision_requested' && idea.has_been_revised && (
                                            <span className="revised-inline-badge">Revised</span>
                                          )}
                                          {idea.status === 'denied' && (
                                            <span className="denied-inline-badge">Client Denied</span>
                                          )}
                                        </div>
                                        <div className="idea-synopsis">{synopsis}</div>
                                      </div>
                                    </td>
                                    <td className="source-cell">
                                      {getSourceOriginBadge(idea)}
                                    </td>
                                    <td className="date-cell">
                                      {formatDate(idea.actionDate)}
                                    </td>
                                    <td className="comment-cell">
                                      <button
                                        className="comment-icon-btn"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setCommentModalState({
                                            open: true,
                                            docId: idea.docId,
                                            ideaId: idea.id,
                                            comments: idea.comments || []
                                          });
                                        }}
                                        title="Add comment"
                                        type="button"
                                      >
                                        <Icon name="comment-empty" size="1.5rem" />
                                        {idea.comments && idea.comments.length > 0 && (
                                          <span className="comment-count-badge">{idea.comments.length}</span>
                                        )}
                                      </button>
                                    </td>
                                    <td className="tags-cell">
                                      {getContentTypeBadge(idea.content_type)}
                                    </td>
                                    <td className="actions-cell">
                                      <div className="idea-actions">
                                        <button
                                          className={`approve-btn approve ${isApprovedIdea ? 'batched' : ''}`}
                                          onClick={() => stageIdeaForSend(idea.docId, idea.id)}
                                          title={isApprovedIdea ? 'Remove from send batch' : 'Add to send batch'}
                                        >
                                          <Icon name="ok" size="1.25rem" />
                                        </button>
                                        <button
                                          className="approve-btn edit"
                                          onClick={() => openEditModal(idea.docId, idea)}
                                          title="Edit idea"
                                        >
                                          <Icon name="pencil" size="1.25rem" />
                                        </button>
                                        <button
                                          className={`approve-btn discard ${isDiscardedIdea ? 'batched-discard' : ''}`}
                                          onClick={() => stageIdeaForDiscard(idea.docId, idea.id)}
                                          title={isDiscardedIdea ? 'Remove from discard batch' : 'Add to discard batch'}
                                        >
                                          <Icon name="cancel" size="1.25rem" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
      </div>

      {/* Edit Idea Modal */}
      {editModal.open && (
        <div className="edit-modal-overlay">
          <div className={`edit-modal edit-modal-split ${sourceViewer.open ? 'with-source-viewer' : ''}`}>
          <div className="edit-modal-form-col">
            <div className="edit-modal-header">
              <h3>Edit Idea</h3>
              <button
                className="modal-close-btn"
                onClick={closeEditModal}
                aria-label="Close"
              />
            </div>
            <div className="edit-modal-content">
              {editModal.idea?.flagged && (
                <div className="flag-banner">
                  <Icon name="flag" size="1rem" className="flag-banner-icon" />
                  <div className="flag-banner-text">
                    <div className="flag-banner-title">Needs Review</div>
                    <div className="flag-banner-reason">
                      {editModal.idea.flag_reason || 'This idea was flagged for review.'}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="flag-banner-clear-btn"
                    onClick={handleClearFlag}
                    disabled={clearingFlag}
                  >
                    {clearingFlag ? 'Clearing…' : 'Clear Flag'}
                  </button>
                </div>
              )}
              <div className="edit-form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter title..."
                />
              </div>

              <div className="edit-form-group">
                <label>Synopsis</label>
                <textarea
                  value={editForm.synopsis}
                  onChange={(e) => setEditForm(prev => ({ ...prev, synopsis: e.target.value }))}
                  placeholder="Enter synopsis..."
                  rows={3}
                />
              </div>

              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label>Content Type</label>
                  <CustomSelect
                    value={editForm.content_type}
                    onChange={(e) => setEditForm(prev => ({ ...prev, content_type: e.target.value }))}
                  >
                    <option value="social">Social</option>
                    <option value="blog">Blog</option>
                  </CustomSelect>
                </div>

                <div className="edit-form-group">
                  <label>Length</label>
                  <CustomSelect
                    value={editForm.length}
                    onChange={(e) => setEditForm(prev => ({ ...prev, length: e.target.value }))}
                  >
                    <option value="short">Short ({(WORD_COUNT_RANGES[editForm.content_type] || WORD_COUNT_RANGES.social).short} words)</option>
                    <option value="medium">Medium ({(WORD_COUNT_RANGES[editForm.content_type] || WORD_COUNT_RANGES.social).medium} words)</option>
                    <option value="long">Long ({(WORD_COUNT_RANGES[editForm.content_type] || WORD_COUNT_RANGES.social).long} words)</option>
                  </CustomSelect>
                </div>
              </div>

              <div className="edit-form-group">
                <label>Key Points</label>
                <div className="key-points-editor">
                  {editForm.key_points.map((point, index) => (
                    <div key={index} className="key-point-row">
                      <input
                        type="text"
                        value={point}
                        onChange={(e) => updateKeyPoint(index, e.target.value)}
                        placeholder={`Point ${index + 1}`}
                      />
                      <button
                        type="button"
                        className="btn-remove-point"
                        onClick={() => removeKeyPoint(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn-add-point"
                    onClick={addKeyPoint}
                  >
                    + Add Key Point
                  </button>
                </div>
              </div>

              <div className="edit-form-group">
                <label>Sources</label>
                <div className="edit-idea-sources">
                  {editForm.sources.map(source => {
                    const isActiveInViewer = sourceViewer.open && sourceViewer.source &&
                      (sourceViewer.source._id === source._id);
                    return (
                      <div
                        key={source._id}
                        className={`edit-idea-source-item ${isActiveInViewer ? 'active' : ''}`}
                      >
                        <span
                          className="edit-idea-source-icon-badge"
                          title={getSourceTypeName(source)}
                        >
                          <Icon name={getSourceIcon(source.source_type)} size="0.8rem" />
                        </span>
                        <span className="edit-idea-source-name">{source.name}</span>
                        <span className="edit-idea-source-date">{formatDate(source.created_at)}</span>
                        <button
                          type="button"
                          className="edit-idea-source-view-btn"
                          onClick={() => viewIdeaSource(source)}
                          title="View source"
                        >
                          <Icon name="eye" size="0.9rem" />
                        </button>
                        {editForm.additional_source_ids.includes(source._id) && (
                          <button
                            type="button"
                            className="btn-remove-point"
                            onClick={() => removeAddedSource(source._id)}
                            title="Remove source"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {editForm.sources.length === 0 && (
                    <p className="edit-idea-no-sources">No sources attached to this idea.</p>
                  )}
                  <button
                    type="button"
                    className="btn-add-point"
                    onClick={openSourcePicker}
                  >
                    + Add Source
                  </button>
                </div>
              </div>

              <div className="edit-modal-actions">
                <button
                  className="btn-cancel"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button
                  className="btn-save"
                  onClick={handleSaveEdit}
                  disabled={saving || isEditFormUnchanged()}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>

          {sourceViewer.open && (
            <div className="edit-modal-source-col">
              <div className="source-pane-header">
                <div className="source-pane-header-info">
                  <h4>{sourceViewer.source?.name || 'Loading source…'}</h4>
                  {sourceViewer.source && (
                    <div className="source-pane-meta">
                      <span className="source-pane-type-badge">
                        <Icon name={getSourceIcon(sourceViewer.source.source_type)} size="0.75rem" />
                        {getSourceTypeName(sourceViewer.source)}
                      </span>
                      <span className="source-pane-meta-divider">•</span>
                      <span>{formatDate(sourceViewer.source.created_at)}</span>
                    </div>
                  )}
                </div>
                <div className="source-pane-header-actions">
                  {sourceViewer.source?.content && (
                    <button
                      className={`source-pane-copy-btn ${sourceViewer.copied ? 'copied' : ''}`}
                      onClick={copyIdeaSourceContent}
                    >
                      <Icon name={sourceViewer.copied ? 'ok' : 'docs'} size="0.85rem" />
                      {sourceViewer.copied ? 'Copied!' : 'Copy'}
                    </button>
                  )}
                  <button className="modal-close-btn" onClick={closeSourceViewer} aria-label="Close source viewer" />
                </div>
              </div>
              <div className="source-pane-body">
                {sourceViewer.loading ? (
                  <p className="source-pane-empty">Loading source…</p>
                ) : !sourceViewer.source ? (
                  <p className="source-pane-empty">Could not load this source.</p>
                ) : (
                  renderSourceContent(sourceViewer.source)
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {/* Add Source Picker Modal */}
      {sourcePicker.open && (
        <div className="edit-modal-overlay">
          <div className="edit-modal source-picker-modal">
            <div className="edit-modal-header">
              <h3>Add Source</h3>
              <button
                className="modal-close-btn"
                onClick={closeSourcePicker}
                aria-label="Close"
              />
            </div>
            <div className="edit-modal-content">
              {sourcePicker.loading ? (
                <p className="edit-idea-no-sources">Loading sources...</p>
              ) : sourcePicker.available.length === 0 ? (
                <p className="edit-idea-no-sources">No additional sources available for this brand.</p>
              ) : (
                <div className="source-picker-list">
                  {sourcePicker.available.map(source => (
                    <label key={source._id} className="source-picker-item">
                      <input
                        type="checkbox"
                        checked={sourcePicker.selectedIds.includes(source._id)}
                        onChange={() => toggleSourcePickerSelect(source._id)}
                      />
                      <span className="edit-idea-source-badge">{getSourceTypeName(source)}</span>
                      <span className="edit-idea-source-name">{source.name}</span>
                      <span className="edit-idea-source-date">{formatDate(source.created_at)}</span>
                    </label>
                  ))}
                </div>
              )}

              <div className="edit-modal-actions">
                <button className="btn-cancel" onClick={closeSourcePicker}>
                  Cancel
                </button>
                <button
                  className="btn-save"
                  onClick={confirmAddSources}
                  disabled={sourcePicker.selectedIds.length === 0}
                >
                  Add {sourcePicker.selectedIds.length > 0 ? sourcePicker.selectedIds.length : ''} Source{sourcePicker.selectedIds.length === 1 ? '' : 's'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recipient Selector Modal */}
      <RecipientSelectorModal
        isOpen={recipientModal.open}
        onClose={() => setRecipientModal({ open: false, docId: null, ideaIds: [], allDocIds: null })}
        onConfirm={handleRecipientsSelected}
        itemType="ideas"
        itemId={recipientModal.docId}
        itemCount={
          recipientModal.allDocIds
            ? recipientModal.allDocIds.reduce((sum, docId) => {
                const neutral = selectedIdeas[docId] || [];
                const approved = approvedIdeas[docId] || [];
                return sum + [...new Set([...neutral, ...approved])].length;
              }, 0)
            : (recipientModal.ideaIds?.length || 0)
        }
      />

      {/* Comment Modal */}
      <CommentModal
        isOpen={commentModalState.open}
        onClose={() => setCommentModalState({ open: false, docId: null, ideaId: null, comments: [] })}
        itemId={commentModalState.ideaId}
        itemType="idea"
        docId={commentModalState.docId}
        comments={commentModalState.comments}
        user={user}
        onCommentAdded={(updatedComments) => {
          setIdeasDocs(prevDocs => prevDocs.map(doc =>
            doc._id === commentModalState.docId
              ? {
                  ...doc,
                  ideas: doc.ideas.map(idea =>
                    idea.id === commentModalState.ideaId
                      ? { ...idea, comments: updatedComments }
                      : idea
                  )
                }
              : doc
          ));
        }}
        onCommentDeleted={(updatedComments) => {
          setIdeasDocs(prevDocs => prevDocs.map(doc =>
            doc._id === commentModalState.docId
              ? {
                  ...doc,
                  ideas: doc.ideas.map(idea =>
                    idea.id === commentModalState.ideaId
                      ? { ...idea, comments: updatedComments }
                      : idea
                  )
                }
              : doc
          ));
        }}
        isProxyMode={isProxyMode}
      />
    </div>
  );
};

export default IdeasPanel;
